// Loads products from the /api/products endpoint and exposes them to
// the public pages (Collections, Gallery, ProductPage). Replaces the
// old static `import products from "../assets/products.json"`.
//
// API returns the same shape as the old JSON: a `{ products: [...] }`
// envelope, each product with `name: { en, pt }`, `description: { en, pt }`,
// etc., so callers don't need to change their expectations.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

const ProductsContext = createContext(null);

export const useProducts = () => {
  const ctx = useContext(ProductsContext);
  if (!ctx) {
    throw new Error("useProducts must be used within a ProductsProvider");
  }
  return ctx;
};

export const ProductsProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/products", { credentials: "include" });
      if (!res.ok) throw new Error(`Failed to load products (${res.status})`);
      const data = await res.json();
      setProducts(Array.isArray(data?.products) ? data.products : []);
    } catch (err) {
      console.error("Failed to load products", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <ProductsContext.Provider
      value={{ products, loading, error, refetch: load }}
    >
      {children}
    </ProductsContext.Provider>
  );
};
